import { useEffect, useRef, useState } from "react";
import { Navigate } from "react-router-dom";
import { api } from "@/lib/apiClient";
import { useAuth } from "@/context/AuthContext";
import Navbar from "@/components/Navbar";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Trash2, Upload, Loader2, CheckCircle2, Plus, Sparkles, UserPlus, X, DollarSign } from "lucide-react";
import { toast } from "sonner";
import AdminPricing from "@/components/AdminPricing";
import AdminPartners from "@/components/AdminPartners";
import AdminPartnerCopy from "@/components/AdminPartnerCopy";
import AdminBlueprint from "@/components/AdminBlueprint";
import AdminBrainKeys from "@/components/AdminBrainKeys";
import AdminLearnMore from "@/components/AdminLearnMore";
import AdminLongInstallations from "@/components/AdminLongInstallations";
import AdminMusic from "@/components/AdminMusic";
import AdminLaunchEmail from "@/components/AdminLaunchEmail";
import ImmersionInsights from "@/components/immersion/ImmersionInsights";

export default function Admin() {
  const { user, refreshUser } = useAuth();
  const [items, setItems] = useState([]);
  const [saving, setSaving] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [uploadedName, setUploadedName] = useState("");
  const fileRef = useRef(null);
  const empty = { title: "", theme: "", description: "", duration: "", cover: "", media_url: "", media_type: "audio", affirmations: "", voice: "sage", script: "" };
  const [form, setForm] = useState(empty);
  const [kbItems, setKbItems] = useState([]);
  const [kb, setKb] = useState({ title: "", content: "" });
  const [kbSaving, setKbSaving] = useState(false);
  const [kbUploading, setKbUploading] = useState(false);
  const kbFileRef = useRef(null);

  const [compList, setCompList] = useState([]);
  const [compEmail, setCompEmail] = useState("");
  const [compSaving, setCompSaving] = useState(false);
  const [costs, setCosts] = useState(null);
  const [grantEmail, setGrantEmail] = useState("");
  const [grantMinutes, setGrantMinutes] = useState(500);
  const [granting, setGranting] = useState(false);
  const grantCredits = async () => {
    setGranting(true);
    try {
      const { data } = await api.post("/admin/grant-credits", {
        email: grantEmail.trim() || (user?.email || ""),
        minutes: Number(grantMinutes) || 500,
      });
      toast.success(`Added ${data.granted_minutes} min to ${data.email}. Talk balance ${data.talk_bonus_minutes} min · Avatar ${data.avatar_bonus_minutes} min.`);
      loadCosts();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not grant credits."); }
    finally { setGranting(false); }
  };
  const loadCosts = () => api.get("/admin/usage-costs").then((r) => setCosts(r.data)).catch(() => {});
  const loadComp = () => api.get("/admin/comp-access").then((r) => setCompList(r.data.members)).catch(() => {});
  const addComp = async () => {
    if (!compEmail.trim()) { toast.error("Enter an email address."); return; }
    setCompSaving(true);
    try {
      const { data } = await api.post("/admin/comp-access", { email: compEmail.trim() });
      toast.success(data.upgraded_existing_user ? "Granted Premium — existing account upgraded." : "Premium access granted.");
      setCompEmail(""); loadComp();
    } catch (err) { toast.error(err?.response?.data?.detail || "Could not add."); } finally { setCompSaving(false); }
  };
  const removeComp = async (email) => {
    try { await api.delete(`/admin/comp-access/${encodeURIComponent(email)}`); loadComp(); toast.success("Access removed."); }
    catch (err) { toast.error(err?.response?.data?.detail || "Could not remove."); }
  };

  const loadKb = () => api.get("/admin/knowledge").then((r) => setKbItems(r.data)).catch(() => {});
  const addKb = async () => {
    if (!kb.content.trim()) { toast.error("Add something for Xcelio to learn."); return; }
    setKbSaving(true);
    try { await api.post("/admin/knowledge", kb); setKb({ title: "", content: "" }); loadKb(); toast.success("Xcelio learned it."); }
    catch { toast.error("Could not save."); } finally { setKbSaving(false); }
  };
  const uploadKb = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setKbUploading(true);
    try { const fd = new FormData(); fd.append("file", file); const { data } = await api.post("/admin/knowledge/upload", fd, { headers: { "Content-Type": "multipart/form-data" } }); toast.success(`Learned "${data.title}" (${data.chars} chars)`); loadKb(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Upload failed."); } finally { setKbUploading(false); if (kbFileRef.current) kbFileRef.current.value = ""; }
  };
  const removeKb = async (id) => { try { await api.delete(`/admin/knowledge/${id}`); loadKb(); } catch { toast.error("Could not delete."); } };

  const load = () => api.get("/installations").then((r) => setItems(r.data)).catch(() => {});
  useEffect(() => { load(); loadKb(); loadComp(); loadCosts(); refreshUser(); /* eslint-disable-next-line */ }, []);
  if (user && !user.is_admin) return <Navigate to="/app" replace />;
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const upload = async (e) => {
    const file = e.target.files?.[0]; if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const { data } = await api.post("/admin/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      set("media_url", data.url); set("media_type", (data.content_type || "").startsWith("video") ? "video" : "audio");
      setUploadedName(file.name); toast.success("Media uploaded.");
    } catch { toast.error("Upload failed."); } finally { setUploading(false); }
  };

  const save = async () => {
    if (!form.title || !form.theme) { toast.error("Add a title and theme."); return; }
    if (!form.media_url && !form.script) { toast.error("Upload media or add a script for TTS."); return; }
    setSaving(true);
    try {
      await api.post("/admin/installations", { ...form, affirmations: form.affirmations.split("\n").map((s) => s.trim()).filter(Boolean) });
      toast.success("Published to Inner Architecture."); setForm(empty); setUploadedName(""); load();
    } catch { toast.error("Could not save."); } finally { setSaving(false); }
  };

  const remove = async (id) => { try { await api.delete(`/admin/installations/${id}`); load(); toast.success("Removed."); } catch { toast.error("Could not delete."); } };

  return (
    <div className="min-h-screen bg-background">
      <Navbar />
      <main className="max-w-5xl mx-auto px-5 sm:px-8 py-10">
        <div className="mb-10">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Xcelio Content Studio</p>
          <h1 className="font-serif text-4xl sm:text-5xl">Inner Architecture admin</h1>
          <p className="text-muted-foreground mt-2 max-w-xl">Upload real affirmation audios and direct .mp4 subliminal/immersion videos, then publish them as installations.</p>
        </div>
        <AdminBlueprint />
        <AdminLaunchEmail />
        <ImmersionInsights admin />

        <div className="grid lg:grid-cols-2 gap-8">
          <div className="rounded-2xl border border-border p-7">
            <h3 className="font-serif text-2xl mb-6 flex items-center gap-2"><Plus className="w-5 h-5 text-primary" /> New installation</h3>
            <div className="space-y-4">
              <div>
                <Label>Media (audio .mp3 / video .mp4) — optional if using script</Label>
                <input ref={fileRef} type="file" accept="audio/*,video/*" onChange={upload} className="hidden" data-testid="admin-file-input" />
                <button type="button" onClick={() => fileRef.current?.click()} data-testid="admin-upload-btn"
                  className="mt-1.5 w-full border border-dashed border-border rounded-xl p-4 flex items-center justify-center gap-2 text-sm text-muted-foreground hover:text-foreground hover:border-primary transition-colors">
                  {uploading ? <Loader2 className="w-4 h-4 animate-spin" /> : uploadedName ? <CheckCircle2 className="w-4 h-4 text-primary" /> : <Upload className="w-4 h-4" />}
                  {uploading ? "Uploading…" : uploadedName || "Click to upload media"}
                </button>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label htmlFor="t">Title</Label><Input id="t" value={form.title} onChange={(e) => set("title", e.target.value)} className="mt-1.5" data-testid="admin-title-input" /></div>
                <div><Label htmlFor="th">Theme</Label><Input id="th" placeholder="e.g. Nothing to Prove" value={form.theme} onChange={(e) => set("theme", e.target.value)} className="mt-1.5" data-testid="admin-theme-input" /></div>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div><Label htmlFor="d">Duration</Label><Input id="d" placeholder="12 min" value={form.duration} onChange={(e) => set("duration", e.target.value)} className="mt-1.5" /></div>
                <div><Label>Type</Label>
                  <Select value={form.media_type} onValueChange={(v) => set("media_type", v)}>
                    <SelectTrigger className="mt-1.5" data-testid="admin-type-select"><SelectValue /></SelectTrigger>
                    <SelectContent><SelectItem value="audio">Audio</SelectItem><SelectItem value="video">Video (immersion)</SelectItem></SelectContent>
                  </Select>
                </div>
              </div>
              <div><Label htmlFor="desc">Description</Label><Textarea id="desc" rows={2} value={form.description} onChange={(e) => set("description", e.target.value)} className="mt-1.5" /></div>
              <div><Label htmlFor="cov">Cover image URL (optional)</Label><Input id="cov" value={form.cover} onChange={(e) => set("cover", e.target.value)} className="mt-1.5" placeholder="https://…" /></div>
              <div><Label htmlFor="af">Affirmations (one per line)</Label><Textarea id="af" rows={3} value={form.affirmations} onChange={(e) => set("affirmations", e.target.value)} className="mt-1.5" placeholder="I have nothing to prove.&#10;I am enough." /></div>
              <div><Label htmlFor="sc">Script (only if no media — used for AI narration)</Label><Textarea id="sc" rows={3} value={form.script} onChange={(e) => set("script", e.target.value)} className="mt-1.5" /></div>
              <Button onClick={save} disabled={saving} className="w-full rounded-full" data-testid="admin-save-btn">{saving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Publish"}</Button>
            </div>
          </div>

          <div>
            <h3 className="font-serif text-2xl mb-6">Library ({items.length})</h3>
            <div className="space-y-3">
              {items.map((r, i) => (
                <div key={`${r.id}-${i}`} className="flex items-center gap-3 rounded-xl border border-border p-3" data-testid={`admin-installation-${r.id}`}>
                  <img src={r.cover} alt="" className="w-12 h-12 rounded-lg object-cover" />
                  <div className="flex-1 min-w-0"><div className="font-medium truncate">{r.title}</div><div className="text-xs text-muted-foreground">{r.theme} · {r.source === "custom" ? (r.media_type || "media") : "built-in / TTS"}</div></div>
                  <Button variant="ghost" size="icon" onClick={() => remove(r.id)} data-testid={`admin-delete-${r.id}`} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="w-4 h-4" /></Button>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Coach Knowledge Base */}
        <div className="mt-14 pt-10 border-t border-border">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Teach Xcelio</p>
          <h2 className="font-serif text-3xl sm:text-4xl mb-2">Coach Knowledge Base</h2>
          <p className="text-muted-foreground mb-8 max-w-xl">Add your own research, frameworks, scripts and language. Xcelio weaves these into her coaching (she keeps her full ability too).</p>
          <div className="grid lg:grid-cols-2 gap-8">
            <div className="rounded-2xl border border-border p-7 space-y-4">
              <div><Label htmlFor="kbt">Title</Label><Input id="kbt" value={kb.title} onChange={(e) => setKb((k) => ({ ...k, title: e.target.value }))} placeholder="e.g. The 3 Presence Keys" className="mt-1.5" data-testid="kb-title-input" /></div>
              <div><Label htmlFor="kbc">Teaching / framework / script</Label><Textarea id="kbc" rows={6} value={kb.content} onChange={(e) => setKb((k) => ({ ...k, content: e.target.value }))} placeholder="Paste your method, philosophy, phrases Xcelio should use…" className="mt-1.5 resize-none" data-testid="kb-content-input" /></div>
              <div className="flex gap-2">
                <Button onClick={addKb} disabled={kbSaving} className="rounded-full flex-1" data-testid="kb-save-btn">{kbSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : "Teach Xcelio"}</Button>
                <input ref={kbFileRef} type="file" accept=".pdf,.txt,.md" onChange={uploadKb} className="hidden" data-testid="kb-file-input" />
                <Button variant="secondary" onClick={() => kbFileRef.current?.click()} disabled={kbUploading} className="rounded-full" data-testid="kb-upload-btn">
                  {kbUploading ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Upload className="w-4 h-4 mr-1.5" /> PDF / TXT</>}
                </Button>
              </div>
            </div>
            <div>
              <h3 className="font-serif text-2xl mb-6">What Xcelio knows ({kbItems.length})</h3>
              <div className="space-y-3">
                {kbItems.length === 0 && <p className="text-sm text-muted-foreground">Nothing yet — add your first teaching.</p>}
                {kbItems.map((r, i) => (
                  <div key={`${r.id}-${i}`} className="flex items-start gap-3 rounded-xl border border-border p-3" data-testid={`kb-item-${r.id}`}>
                    <div className="flex-1 min-w-0">
                      <div className="font-medium truncate">{r.title}</div>
                      <div className="text-xs text-muted-foreground line-clamp-2">{r.content}</div>
                      <div className="text-[10px] uppercase tracking-wider text-primary/70 mt-1">{r.source}</div>
                    </div>
                    <Button variant="ghost" size="icon" onClick={() => removeKb(r.id)} data-testid={`kb-delete-${r.id}`} className="text-muted-foreground hover:text-destructive shrink-0"><Trash2 className="w-4 h-4" /></Button>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>

        <AdminPricing />
        <AdminPartners />
        <AdminPartnerCopy />
        <AdminLongInstallations />
        <AdminMusic />
        <AdminBrainKeys />
        <AdminLearnMore />

        {/* Testing credits — instant top-up for QA */}
        <div className="mt-14 pt-10 border-t border-border">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Testing credits</p>
          <h2 className="font-serif text-3xl sm:text-4xl mb-2 flex items-center gap-2"><Sparkles className="w-6 h-6 text-primary" /> Load minutes for testing</h2>
          <p className="text-muted-foreground mb-8 max-w-xl">Instantly add never-expiring talk + live-avatar minutes to any account so you can test freely. Leave the email blank to top up your own account. This also resets the current week/month usage so the buffer is usable right away.</p>
          <div className="max-w-xl flex flex-col sm:flex-row gap-2">
            <Input value={grantEmail} onChange={(e) => setGrantEmail(e.target.value)} placeholder={user?.email || "your@email.com"} type="email" className="flex-1" data-testid="grant-email-input" />
            <Input value={grantMinutes} onChange={(e) => setGrantMinutes(e.target.value)} type="number" min="1" className="sm:w-32" data-testid="grant-minutes-input" />
            <Button onClick={grantCredits} disabled={granting} className="rounded-full" data-testid="grant-credits-btn">
              {granting ? <Loader2 className="w-4 h-4 animate-spin" /> : <><Plus className="w-4 h-4 mr-1.5" /> Add minutes</>}
            </Button>
          </div>
        </div>

        {/* Founder Access — grant free Premium by email */}
        <div className="mt-14 pt-10 border-t border-border">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Founder access</p>
          <h2 className="font-serif text-3xl sm:text-4xl mb-2 flex items-center gap-2"><Sparkles className="w-6 h-6 text-primary" /> Grant free Premium</h2>
          <p className="text-muted-foreground mb-8 max-w-xl">Give any friend or partner full Premium access by email — no payment needed. If they already have an account they're upgraded instantly; otherwise they're upgraded the moment they sign up.</p>
          <div className="max-w-xl">
            <div className="flex gap-2 mb-6">
              <Input value={compEmail} onChange={(e) => setCompEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && addComp()}
                placeholder="friend@email.com" type="email" className="flex-1" data-testid="comp-email-input" />
              <Button onClick={addComp} disabled={compSaving} className="rounded-full" data-testid="comp-add-btn">
                {compSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : <><UserPlus className="w-4 h-4 mr-1.5" /> Grant</>}
              </Button>
            </div>
            <div className="space-y-2" data-testid="comp-list">
              {compList.length === 0 && <p className="text-sm text-muted-foreground">No one added yet.</p>}
              {compList.map((m) => (
                <div key={m.email} className="flex items-center gap-3 rounded-xl border border-border p-3 bg-card/50" data-testid={`comp-item-${m.email}`}>
                  <span className="flex items-center justify-center w-8 h-8 rounded-full bg-teal-400/20 text-teal-600 shrink-0"><Sparkles className="w-4 h-4" /></span>
                  <div className="flex-1 min-w-0">
                    <div className="font-medium truncate">{m.email}</div>
                    <div className="text-xs text-muted-foreground">
                      {m.builtin ? "Built-in founder" : "Granted by you"} · {m.registered ? "account active" : "not signed up yet"}
                    </div>
                  </div>
                  {m.builtin ? (
                    <span className="text-[10px] uppercase tracking-wider text-muted-foreground/60 shrink-0">Locked</span>
                  ) : (
                    <Button variant="ghost" size="icon" onClick={() => removeComp(m.email)} data-testid={`comp-remove-${m.email}`} className="text-muted-foreground hover:text-destructive shrink-0"><X className="w-4 h-4" /></Button>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>
        {/* Cost meter — real spend in £, pack economics & per-session cost */}
        <div className="mt-14 pt-10 border-t border-border">
          <p className="text-xs uppercase tracking-[0.2em] text-primary mb-2">Cost meter</p>
          <h2 className="font-serif text-3xl sm:text-4xl mb-2 flex items-center gap-2"><DollarSign className="w-6 h-6 text-primary" /> Real cost & pack pricing</h2>
          <p className="text-muted-foreground mb-6 max-w-xl">Live voice cost in £ (converted at {costs?.fx?.gbp_per_usd ?? "0.79"} USD→GBP). Use the blended cost/min and pack economics below to price your top-up packs confidently.</p>

          {costs && (
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 mb-8">
              <div className="rounded-2xl border border-border p-4 bg-card/50"><div className="text-2xl font-serif">{costs.totals.talk_minutes}</div><div className="text-xs text-muted-foreground">talk minutes</div></div>
              <div className="rounded-2xl border border-border p-4 bg-card/50"><div className="text-2xl font-serif text-primary">£{costs.totals.est_cost_gbp}</div><div className="text-xs text-muted-foreground">total spend</div></div>
              <div className="rounded-2xl border border-primary/30 p-4 bg-primary/5"><div className="text-2xl font-serif text-primary">£{costs.totals.est_cost_per_min_gbp}</div><div className="text-xs text-muted-foreground">cost / talk min</div></div>
              <div className="rounded-2xl border border-border p-4 bg-card/50"><div className="text-2xl font-serif">{costs.totals.users}</div><div className="text-xs text-muted-foreground">users tracked</div></div>
            </div>
          )}

          {/* Pack economics — the pricing tool */}
          <h3 className="font-serif text-2xl mb-3">Top-up pack economics</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-xl">Cost to serve each pack (at the blended £/min above) vs its price — so you can see the margin before you finalise pricing.</p>
          <div className="rounded-2xl border border-border overflow-hidden mb-10" data-testid="pack-economics">
            <div className="grid grid-cols-5 gap-2 px-4 py-2.5 bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
              <span>Pack</span><span>Price</span><span>Cost to serve</span><span>Profit</span><span className="text-right">Margin</span>
            </div>
            {(!costs || !costs.packs?.length) && <div className="px-4 py-4 text-sm text-muted-foreground">Run some talk minutes to populate cost/min.</div>}
            {costs?.packs?.map((p) => (
              <div key={p.key} className="grid grid-cols-5 gap-2 px-4 py-3 text-sm border-t border-border items-center" data-testid={`pack-row-${p.key}`}>
                <span>{p.minutes} min</span>
                <span>£{p.price_gbp.toFixed(2)}</span>
                <span className="text-muted-foreground">£{p.cost_to_serve_gbp.toFixed(2)}</span>
                <span className={p.profit_gbp >= 0 ? "text-emerald-500 font-medium" : "text-destructive font-medium"}>£{p.profit_gbp.toFixed(2)}</span>
                <span className={`text-right font-medium ${p.margin_pct >= 0 ? "text-primary" : "text-destructive"}`}>{p.margin_pct}%</span>
              </div>
            ))}
          </div>

          {/* Per-session cost */}
          <h3 className="font-serif text-2xl mb-3">Recent sessions</h3>
          <p className="text-sm text-muted-foreground mb-4 max-w-xl">The real £ cost of each individual voice call (talk & roleplay), newest first.</p>
          <div className="rounded-2xl border border-border overflow-hidden mb-10" data-testid="session-cost-table">
            <div className="grid grid-cols-5 gap-2 px-4 py-2.5 bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
              <span className="col-span-2">User</span><span>Type</span><span>Minutes</span><span className="text-right">Cost</span>
            </div>
            {(!costs || !costs.sessions?.length) && <div className="px-4 py-4 text-sm text-muted-foreground">No voice sessions recorded yet.</div>}
            {costs?.sessions?.map((s, i) => (
              <div key={`${s.id}-${i}`} className="grid grid-cols-5 gap-2 px-4 py-3 text-sm border-t border-border items-center" data-testid={`session-row-${s.id}`}>
                <span className="col-span-2 truncate">{s.email}</span>
                <span className="text-muted-foreground capitalize">{s.kind}</span>
                <span>{s.minutes} <span className="text-muted-foreground text-xs">· {s.turns} turns</span></span>
                <span className="text-right font-medium text-primary">£{s.est_cost_gbp}</span>
              </div>
            ))}
          </div>

          {/* Per-user cost */}
          <h3 className="font-serif text-2xl mb-3">Cost per user</h3>
          <div className="rounded-2xl border border-border overflow-hidden" data-testid="cost-table">
            <div className="grid grid-cols-5 gap-2 px-4 py-2.5 bg-muted/40 text-[11px] uppercase tracking-wider text-muted-foreground">
              <span className="col-span-2">User</span><span>Plan</span><span>Talk min</span><span className="text-right">Est. cost</span>
            </div>
            {(!costs || costs.users.length === 0) && <div className="px-4 py-4 text-sm text-muted-foreground">No voice usage recorded yet.</div>}
            {costs?.users.map((r, i) => (
              <div key={`${r.email}-${i}`} className="grid grid-cols-5 gap-2 px-4 py-3 text-sm border-t border-border items-center" data-testid={`cost-row-${r.email}`}>
                <span className="col-span-2 truncate">{r.email}</span>
                <span className="text-muted-foreground capitalize">{r.plan}</span>
                <span>{r.talk_minutes}</span>
                <span className="text-right font-medium text-primary">£{r.est_cost_gbp}</span>
              </div>
            ))}
          </div>
        </div>
      </main>
    </div>
  );
}
